import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  Eye,
  Share2,
  Phone,
  TrendingUp,
  Loader2,
  Copy,
  Facebook,
  MessageSquare,
  Smartphone,
  BarChart3,
  Car,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatPrice = (price) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(price || 0);

const formatDate = (iso) => {
  if (!iso) return "Jamais";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AdminAnalyticsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("adminToken");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("view_count");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/vehicle-analytics?token=${token}`);
      setData(res.data);
    } catch (e) {
      toast.error("Erreur de chargement des statistiques");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate("/espace-pro-fastandcar");
      return;
    }
    fetchData();
  }, [token, navigate, fetchData]);

  const sortedVehicles = data
    ? [...data.vehicles].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0))
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-neon-lime" />
      </div>
    );
  }

  const totals = data?.totals || {};
  const avgViews =
    totals.total_vehicles > 0
      ? Math.round(totals.total_views / totals.total_vehicles)
      : 0;
  const conversionRate =
    totals.total_views > 0
      ? ((totals.total_contacts / totals.total_views) * 100).toFixed(1)
      : "0.0";

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/espace-pro-fastandcar/dashboard")}
              data-testid="back-to-dashboard"
              className="text-white hover:bg-zinc-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="font-unbounded text-2xl md:text-3xl font-black flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-neon-lime" />
                Statistiques véhicules
              </h1>
              <p className="text-zinc-400 text-sm mt-1">
                Clics, partages et demandes de contact par véhicule
              </p>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="border border-zinc-800 bg-zinc-900/50 p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider mb-2">
              <Eye className="w-4 h-4" /> Vues totales
            </div>
            <p className="font-unbounded text-3xl font-black text-white" data-testid="total-views">
              {totals.total_views || 0}
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              {avgViews} en moyenne/véhicule
            </p>
          </div>
          <div className="border border-zinc-800 bg-zinc-900/50 p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider mb-2">
              <Share2 className="w-4 h-4" /> Partages
            </div>
            <p className="font-unbounded text-3xl font-black text-neon-lime" data-testid="total-shares">
              {totals.total_shares || 0}
            </p>
            <p className="text-xs text-zinc-500 mt-1">Toutes plateformes</p>
          </div>
          <div className="border border-zinc-800 bg-zinc-900/50 p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider mb-2">
              <Phone className="w-4 h-4" /> Demandes de contact
            </div>
            <p className="font-unbounded text-3xl font-black text-orange-400" data-testid="total-contacts">
              {totals.total_contacts || 0}
            </p>
            <p className="text-xs text-zinc-500 mt-1">Depuis les fiches</p>
          </div>
          <div className="border border-zinc-800 bg-zinc-900/50 p-4">
            <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider mb-2">
              <TrendingUp className="w-4 h-4" /> Taux conversion
            </div>
            <p className="font-unbounded text-3xl font-black text-white" data-testid="conversion-rate">
              {conversionRate}%
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              Contacts / Vues
            </p>
          </div>
        </div>

        {/* Sort options */}
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <span className="text-zinc-400 text-sm">Trier par :</span>
          {[
            { key: "view_count", label: "Vues" },
            { key: "share_count", label: "Partages" },
            { key: "contact_count", label: "Contacts" },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setSortBy(opt.key)}
              data-testid={`sort-${opt.key}`}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border ${
                sortBy === opt.key
                  ? "border-neon-lime bg-neon-lime/10 text-neon-lime"
                  : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Table */}
        {sortedVehicles.length === 0 ? (
          <div className="border border-zinc-800 p-16 text-center">
            <Car className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
            <p className="text-zinc-400">Aucun véhicule à analyser pour le moment</p>
          </div>
        ) : (
          <div className="border border-zinc-800 overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-zinc-900 text-left text-xs uppercase tracking-wider text-zinc-400">
                  <th className="p-3">Véhicule</th>
                  <th className="p-3 text-center">
                    <Eye className="w-4 h-4 inline" />
                  </th>
                  <th className="p-3 text-center">
                    <Share2 className="w-4 h-4 inline" />
                  </th>
                  <th className="p-3 text-center" title="WhatsApp">
                    <MessageSquare className="w-4 h-4 inline text-[#25D366]" />
                  </th>
                  <th className="p-3 text-center" title="Facebook">
                    <Facebook className="w-4 h-4 inline text-[#1877F2]" />
                  </th>
                  <th className="p-3 text-center" title="Copie de lien">
                    <Copy className="w-4 h-4 inline text-zinc-400" />
                  </th>
                  <th className="p-3 text-center" title="Partage natif mobile">
                    <Smartphone className="w-4 h-4 inline text-neon-lime" />
                  </th>
                  <th className="p-3 text-center">
                    <Phone className="w-4 h-4 inline text-orange-400" />
                  </th>
                  <th className="p-3 text-right">Dernière vue</th>
                </tr>
              </thead>
              <tbody>
                {sortedVehicles.map((v) => (
                  <tr
                    key={v.id}
                    data-testid={`vehicle-stats-${v.id}`}
                    className="border-t border-zinc-800 hover:bg-zinc-900/30 transition-colors"
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        {v.thumbnail ? (
                          <img
                            src={v.thumbnail}
                            alt=""
                            className="w-12 h-12 object-cover bg-zinc-800 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-zinc-800 flex items-center justify-center flex-shrink-0">
                            <Car className="w-5 h-5 text-zinc-600" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-bold text-white truncate">
                            {v.brand} {v.model}
                          </p>
                          <p className="text-xs text-zinc-500">
                            {v.year} • {formatPrice(v.price)}
                            {v.is_sold && (
                              <span className="ml-2 text-red-400">● VENDUE</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-center font-bold">{v.view_count}</td>
                    <td className="p-3 text-center font-bold text-neon-lime">
                      {v.share_count}
                    </td>
                    <td className="p-3 text-center text-zinc-400">{v.share_whatsapp}</td>
                    <td className="p-3 text-center text-zinc-400">{v.share_facebook}</td>
                    <td className="p-3 text-center text-zinc-400">{v.share_copy}</td>
                    <td className="p-3 text-center text-zinc-400">{v.share_native}</td>
                    <td className="p-3 text-center font-bold text-orange-400">
                      {v.contact_count}
                    </td>
                    <td className="p-3 text-right text-xs text-zinc-500">
                      {formatDate(v.last_viewed_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 text-xs text-zinc-500">
          <p>
            <strong className="text-zinc-400">Astuce :</strong> Les vues sont comptabilisées quand un visiteur ouvre la fiche détaillée d'un véhicule. Les partages sont comptés à chaque clic sur un bouton de partage (WhatsApp, Facebook, copie, natif mobile). Les contacts sont les formulaires envoyés depuis la fiche.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
